import { IntlShape } from "react-intl";

import * as api from "@nens/threedi/openapi-client";

import { getV3Api } from "../apiConfiguration";
import { ENDLESS_SIMULATION_DURATION_LIMIT } from "../state/simulation";

// Helper function to check for permission
interface PermissionAllowed {
  allowed: true;
}
interface PermissionNotAllowed {
  allowed: false;
  errorCode: ErrorCode;
}
export type Permission = PermissionAllowed | PermissionNotAllowed;

export type ErrorCode =
  | "simulationRunning"
  | "masterUserRequired"
  | "NRRExpired"
  | "simulationTimeLimited"
  | "simulationStarted";
export type ControlType =
  | "editAssetProperty"
  | "editBreach"
  | "editRain"
  | "editWind"
  | "editPump"
  | "editDischarge"
  | "editRaster"
  | "startStopSimulation"
  | "rateLimiterSwitchOnOff"
  | "deleteEdit";

export const checkPermissionForRole = async (
  username: string,
  selectedOrganisationName: string,
  organisationId: string,
  role: string
) => {
  const v3Api = getV3Api();
  const hasPermission = await v3Api
    .permissionsList({
      userUsername: username,
      roleNameIn: [role],
      organisationName: selectedOrganisationName,
    })
    .then((res) => res.results.some((result) => result.organisation === organisationId))
    .catch((e) => console.error(e));
  return !!hasPermission;
};

export const checkPermissionStateless = (
  controlType: ControlType,
  user: api.AuthProfile | null,
  simulation: api.Simulation | null,
  status: string
): Permission => {
  // Only the owner of the simulation is allowed to edit things
  if (simulation && (!user || simulation.user !== user.username)) {
    return {
      allowed: false,
      errorCode: "masterUserRequired",
    };
  }

  // While the simulation is running, only stopping it is allowed
  if (status === "running" && controlType !== "startStopSimulation") {
    return {
      allowed: false,
      errorCode: "simulationRunning",
    };
  }

  // Stopping is only allowed for non-endless simulations
  const simulationTimeLimited =
    simulation && simulation.duration && simulation.duration < ENDLESS_SIMULATION_DURATION_LIMIT;

  if (controlType === "startStopSimulation" && simulationTimeLimited) {
    return {
      allowed: false,
      errorCode: "simulationTimeLimited",
    };
  }

  return { allowed: true };
};

export const formattedMessageForErrorCode = (errorCode: string, intl: IntlShape): string => {
  if (errorCode === "simulationRunning") {
    return intl.formatMessage({
      id: "tooltip.simulationRunning",
      defaultMessage: "Only allowed if paused",
    });
  }
  if (errorCode === "masterUserRequired") {
    return intl.formatMessage({
      id: "tooltip.masterUserRequired",
      defaultMessage: "Only master user is allowed to edit",
    });
  }
  if (errorCode === "NRRExpired") {
    return intl.formatMessage({
      id: "tooltip.NRRExpired",
      defaultMessage: "You do not have access to this functionality",
    });
  }

  return "";
};
